import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { APP_NAME } from "../../constants/app";
import { useAuthStore } from "../../stores/authStore";
import { BANNER_BOTTOM_PADDING } from "../ui/BugReporterBanner";
import { Button } from "../ui/Button";

interface PageHeaderProps {
  showNavigation?: boolean;
  children?: ReactNode;
}

interface NavItem {
  to: string;
  label: string;
  adminOnly?: boolean;
}

/** メニューとトグルボタンの間でフォーカスをトラップする */
function trapFocus(
  e: KeyboardEvent,
  menu: HTMLElement | null,
  toggleButton: HTMLElement | null
) {
  if (!menu) {
    return;
  }
  // メニュー内のフォーカス可能要素を取得
  const menuFocusable = Array.from(
    menu.querySelectorAll<HTMLElement>(
      'a[href], button, [tabindex]:not([tabindex="-1"])'
    )
  );
  // トグルボタンも含めてフォーカストラップのスコープを構成
  const allFocusable = toggleButton
    ? [toggleButton, ...menuFocusable]
    : menuFocusable;
  if (allFocusable.length === 0) {
    return;
  }
  const first = allFocusable[0];
  const last = allFocusable[allFocusable.length - 1];
  if (!(first && last)) {
    return;
  }
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

const NAV_ITEMS: NavItem[] = [
  { to: "/seats", label: "座席マップ" },
  { to: "/my-reservations", label: "予約一覧" },
  { to: "/admin/users", label: "ユーザー管理", adminOnly: true },
  { to: "/admin/invites", label: "招待管理", adminOnly: true },
  { to: "/admin/layout", label: "見取り図", adminOnly: true },
  { to: "/admin/seats", label: "座席配置", adminOnly: true },
];

function NavLink({
  to,
  label,
  isActive,
}: { to: string; label: string; isActive: boolean }) {
  return (
    <Link
      to={to}
      className={`text-sm font-medium transition-colors py-3 ${
        isActive
          ? "text-primary-600 border-b-2 border-primary-600"
          : "text-gray-600 hover:text-gray-900"
      }`}
      aria-current={isActive ? "page" : undefined}
    >
      {label}
    </Link>
  );
}

function HamburgerIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      className="h-6 w-6"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      className="h-6 w-6"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18 18 6M6 6l12 12"
      />
    </svg>
  );
}

export function PageHeader({
  showNavigation = true,
  children,
}: PageHeaderProps) {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/auth/login");
  };

  const isAdmin = user?.isAdmin ?? false;
  const visibleItems = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // ルート変更時にメニューを閉じる
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      setIsMobileMenuOpen(false);
      prevPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);

  // モバイルメニュー: Escapeキーで閉じる + フォーカストラップ
  const mobileMenuRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeMobileMenu();
        menuButtonRef.current?.focus();
        return;
      }
      if (e.key === "Tab") {
        trapFocus(e, mobileMenuRef.current, menuButtonRef.current);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    // メニューを開いたとき、最初のリンクにフォーカス
    const firstLink =
      mobileMenuRef.current?.querySelector<HTMLElement>("a[href]");
    firstLink?.focus();

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMobileMenuOpen, closeMobileMenu]);

  return (
    <header className="bg-white shadow relative z-40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Left side: Title + Nav */}
          <div className="flex items-center gap-8">
            <Link
              to="/seats"
              className="flex items-center gap-2 text-xl font-bold text-gray-900 hover:text-primary-600 transition-colors"
            >
              <img
                src="/favicon.png"
                alt=""
                className="h-8 w-8"
                aria-hidden="true"
              />
              {APP_NAME}
            </Link>

            {/* Desktop Navigation */}
            {showNavigation && (
              <nav
                className="hidden lg:flex items-center gap-6"
                aria-label="メインナビゲーション"
              >
                {visibleItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    label={item.label}
                    isActive={location.pathname === item.to}
                  />
                ))}
              </nav>
            )}
          </div>

          {/* Right side: User info and actions */}
          <div className="flex items-center gap-4">
            {children}

            {user && (
              <div className="flex items-center gap-3">
                <Link
                  to="/profile"
                  className="text-sm text-gray-600 hidden sm:inline hover:text-primary-600 transition-colors"
                >
                  {user.name || user.email}
                </Link>
                <Button variant="ghost" size="sm" onClick={handleLogout}>
                  ログアウト
                </Button>
              </div>
            )}

            {/* Mobile menu button */}
            {showNavigation && (
              <button
                ref={menuButtonRef}
                type="button"
                className="lg:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
                aria-label={
                  isMobileMenuOpen ? "メニューを閉じる" : "メニューを開く"
                }
              >
                {isMobileMenuOpen ? <CloseIcon /> : <HamburgerIcon />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {showNavigation && isMobileMenuOpen && (
        <>
          {/* Backdrop - aria-hidden でスクリーンリーダーから非表示 */}
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: バックドロップは装飾的要素。キーボード操作はフォーカストラップで処理 */}
          <div
            className="fixed inset-0 bg-black/30 lg:hidden z-40"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />
          {/* Menu panel */}
          <nav
            ref={mobileMenuRef}
            id="mobile-menu"
            className="absolute top-16 left-0 right-0 bg-white shadow-lg border-t border-gray-200 lg:hidden z-50"
            aria-label="モバイルナビゲーション"
          >
            <div className="px-4 py-2 space-y-1">
              {visibleItems.map((item) => {
                const isActive = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`block px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary-50 text-primary-600"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </nav>
        </>
      )}
    </header>
  );
}

interface PageContainerProps {
  children: ReactNode;
  className?: string;
}

export function PageContainer({
  children,
  className = "",
}: PageContainerProps) {
  return (
    <div
      className={`min-h-screen bg-gray-100 ${BANNER_BOTTOM_PADDING} ${className}`}
    >
      {children}
    </div>
  );
}

interface PageContentProps {
  children: ReactNode;
  className?: string;
}

export function PageContent({ children, className = "" }: PageContentProps) {
  return (
    <main
      className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 animate-fade-in ${className}`}
    >
      {children}
    </main>
  );
}
